import React, { useEffect, useState } from "react";
import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";
import { ChevronRight, ChevronDown, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SidebarMenuCardCst from "@components/SidebarMenuCardCst";
import { SidebarProps, SidebarSimpleItemProps } from "./types";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
  SidebarContainer,
  SidebarHeader,
  SidebarBody,
  SidebarSectionWrapper,
  SidebarSectionLabel,
  SimpleItemWrapper,
  SimpleItemContainer,
  SimpleItemContent,
  SimpleItemIconWrapper,
  SimpleItemChildrenContainer,
  SimpleItemChild,
  CollapsedIconTile,
} from "./style";
import {
  SIDEBAR_HEADER_CORPORATE_TEST_PROPS,
  SIDEBAR_INITIATIVE_SELECT_TEST_PROPS,
  SIDEBAR_NAV_TEST_PROPS,
  getInitiativeOptionTestProps,
  getNavItemTestProps,
  getSectionTestProps,
  withNavIconTestProps,
} from "./testIds";

const SidebarSimpleItem: React.FC<SidebarSimpleItemProps> = ({
  icon,
  title,
  description,
  to,
  active = false,
  testKey,
  onClick,
  hasChevron = true,
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hasActiveChild = !!children?.some((child) => child.active);
  const [expanded, setExpanded] = useState(hasActiveChild);
  const hasChildren = children && children.length > 0;

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  const handleClick = () => {
    if (hasChildren) {
      setExpanded((prev) => !prev);
      return;
    }
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  const handleChildClick = (child: { to?: string; onClick?: () => void }) => {
    if (child.onClick) {
      child.onClick();
    } else if (child.to) {
      navigate(child.to);
    }
  };

  return (
    <SimpleItemWrapper>
      <SimpleItemContainer
        onClick={handleClick}
        sx={
          active
            ? { backgroundColor: theme.palette.action.selected }
            : undefined
        }
        {...getNavItemTestProps(testKey, title)}
      >
        {icon && (
          <SimpleItemIconWrapper>
            {withNavIconTestProps(icon, testKey)}
          </SimpleItemIconWrapper>
        )}
        <SimpleItemContent>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                fontSize: "11px",
                color: theme.palette.text.description,
                lineHeight: 1.3,
                marginTop: "2px",
              }}
            >
              {description}
            </Typography>
          )}
        </SimpleItemContent>
        {hasChevron && (
          <ChevronRight
            size={16}
            color={theme.palette.text.tertiary}
            style={{
              flexShrink: 0,
              transition: "transform 0.2s ease",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        )}
      </SimpleItemContainer>
      {hasChildren && (
        <SimpleItemChildrenContainer
          sx={{
            maxHeight: expanded ? `${children.length * 36}px` : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          {children.map((child, index) => (
            <SimpleItemChild
              key={index}
              nested={child.nested}
              onClick={() => handleChildClick(child)}
              {...getNavItemTestProps(child.testKey, child.title)}
              sx={
                child.active
                  ? { backgroundColor: theme.palette.action.selected }
                  : undefined
              }
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: child.active ? 600 : 400,
                  color: theme.palette.text.primary,
                  lineHeight: 1.4,
                }}
              >
                {child.title}
              </Typography>
            </SimpleItemChild>
          ))}
        </SimpleItemChildrenContainer>
      )}
    </SimpleItemWrapper>
  );
};

interface CollapsedItem {
  key: string;
  title: string;
  icon: React.ReactNode;
  active?: boolean;
  to?: string;
  onClick?: () => void;
  testKey?: string;
  hasChildren: boolean;
  hasActiveChild: boolean;
  firstChildTo?: string;
}

const SidebarCst: React.FC<SidebarProps> = ({
  header,
  sections,
  width,
  collapsed = false,
  onExpandRequest,
  fullHeight = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [initiativeExpanded, setInitiativeExpanded] = useState(false);
  const resolvedWidth =
    width ?? (collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH);

  if (collapsed) {
    const handleItemClick = (item: CollapsedItem) => {
      if (item.hasChildren) {
        onExpandRequest?.();
        if (!item.hasActiveChild && item.firstChildTo) {
          navigate(item.firstChildTo);
        }
        return;
      }
      if (item.onClick) {
        item.onClick();
        return;
      }
      if (item.to) {
        navigate(item.to);
      }
    };

    return (
      <SidebarContainer width={resolvedWidth} fullHeight={fullHeight}>
        <SidebarHeader sx={{ alignItems: "center", padding: "20px 8px 16px" }}>
          <Box
            sx={{ textAlign: "center" }}
            {...SIDEBAR_HEADER_CORPORATE_TEST_PROPS}
          >
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: 600,
                color: theme.palette.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "4px",
              }}
            >
              Corporate
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.palette.primary.main,
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
            >
              {header.corporateName}
            </Typography>
          </Box>

          {header.initiativeOptions && header.initiativeOptions.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: theme.palette.text.tertiary,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "6px",
                }}
              >
                {header.initiativeLabel || "Iniziativa"}
              </Typography>
              <Tooltip
                title={
                  header.initiativeOptions.find(
                    (o) => o.value === header.selectedInitiative,
                  )?.label || ""
                }
                placement="right"
              >
                <CollapsedIconTile
                  onClick={() => onExpandRequest?.()}
                  {...SIDEBAR_INITIATIVE_SELECT_TEST_PROPS}
                >
                  <Building2 size={20} />
                </CollapsedIconTile>
              </Tooltip>
            </Box>
          )}
        </SidebarHeader>

        <Divider sx={{ mx: 2, mb: 1 }} />

        <SidebarBody
          component="nav"
          sx={{ padding: "0 8px 16px", alignItems: "center" }}
          {...SIDEBAR_NAV_TEST_PROPS}
        >
          {sections.map((section, sectionIndex) => {
            const items: CollapsedItem[] = [
              ...(section.type === "cards"
                ? section.items.map((item, i) => ({
                    key: `card-${i}`,
                    title: item.title,
                    icon: item.icon,
                    active: item.active,
                    to: item.to,
                    onClick: item.onClick,
                    testKey: item.testKey,
                    hasChildren: !!item.children && item.children.length > 0,
                    hasActiveChild: !!item.children?.some(
                      (child) => child.active,
                    ),
                    firstChildTo: item.children?.find((child) => child.to)?.to,
                  }))
                : []),
              ...(section.type === "simple"
                ? (section.simpleItems || []).map((item, i) => ({
                    key: `simple-${i}`,
                    title: item.title,
                    icon: item.icon,
                    active: item.active,
                    to: item.to,
                    onClick: item.onClick,
                    testKey: item.testKey,
                    hasChildren: !!item.children && item.children.length > 0,
                    hasActiveChild: !!item.children?.some(
                      (child) => child.active,
                    ),
                    firstChildTo: item.children?.find((child) => child.to)?.to,
                  }))
                : []),
            ];

            if (items.length === 0) return null;

            return (
              <React.Fragment key={sectionIndex}>
                {sectionIndex > 0 && (
                  <Divider sx={{ alignSelf: "stretch", mx: 1, my: 1 }} />
                )}
                <SidebarSectionWrapper sx={{ alignItems: "center" }}>
                  <SidebarSectionLabel
                    sx={{ textAlign: "center", padding: "4px 0 6px" }}
                    {...getSectionTestProps(section.testKey)}
                  >
                    <Typography
                      sx={{
                        fontSize: "9px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        color: theme.palette.text.tertiary,
                        lineHeight: 1.2,
                      }}
                    >
                      {section.label}
                    </Typography>
                  </SidebarSectionLabel>

                  {items.map((item) => (
                    <Tooltip
                      key={item.key}
                      title={item.title}
                      placement="right"
                    >
                      <CollapsedIconTile
                        active={item.active || item.hasActiveChild}
                        onClick={() => handleItemClick(item)}
                        {...getNavItemTestProps(item.testKey, item.title)}
                      >
                        {withNavIconTestProps(item.icon, item.testKey)}
                      </CollapsedIconTile>
                    </Tooltip>
                  ))}
                </SidebarSectionWrapper>
              </React.Fragment>
            );
          })}
        </SidebarBody>
      </SidebarContainer>
    );
  }

  const selectedInitiativeLabel =
    header.initiativeOptions?.find((o) => o.value === header.selectedInitiative)
      ?.label || header.initiativeOptions?.[0]?.label;

  return (
    <SidebarContainer width={resolvedWidth} fullHeight={fullHeight}>
      {/* Header: Corporate + Initiative */}
      <SidebarHeader>
        <Box {...SIDEBAR_HEADER_CORPORATE_TEST_PROPS}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: theme.palette.text.tertiary,
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "4px",
            }}
          >
            Corporate
          </Typography>
          {header.corporateSelector || (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              {header.corporateName}
            </Typography>
          )}
        </Box>

        {header.initiativeOptions && header.initiativeOptions.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                color: theme.palette.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "6px",
              }}
            >
              {header.initiativeLabel || "Iniziativa"}
            </Typography>

            {/* Trigger row: expands the list inline instead of opening a floating dropdown */}
            <Box
              {...SIDEBAR_INITIATIVE_SELECT_TEST_PROPS}
              onClick={() => setInitiativeExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 10px",
                borderRadius: "10px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Building2
                size={16}
                color={theme.palette.text.tertiary}
                style={{ flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedInitiativeLabel}
              </Typography>
              <ChevronDown
                size={16}
                color={theme.palette.text.tertiary}
                style={{
                  flexShrink: 0,
                  transition: "transform 0.2s ease",
                  transform: initiativeExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
              />
            </Box>

            {/* Inline expanding list, same visual language as the section children below */}
            <SimpleItemChildrenContainer
              sx={{
                maxHeight: initiativeExpanded
                  ? `${Math.min(header.initiativeOptions.length * 36, 350)}px`
                  : "0px",
                overflowY: "auto",
                opacity: initiativeExpanded ? 1 : 0,
                position: "relative",
                transition: "max-height 0.2s ease, opacity 0.2s ease",
                "&::-webkit-scrollbar": {
                  width: "1px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.divider,
                  borderRadius: "4px",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: "14px",
                    width: "1px",
                    backgroundColor: theme.palette.divider,
                  },
                }}
              >
                {header.initiativeOptions.map((option) => (
                  <SimpleItemChild
                    key={option.value}
                    {...getInitiativeOptionTestProps(option.value)}
                    onClick={() => {
                      header.onInitiativeChange?.(option.value);
                      setInitiativeExpanded(false);
                    }}
                    sx={{
                      marginLeft: "22px",
                      paddingLeft: "12px",
                      backgroundColor:
                        option.value === header.selectedInitiative
                          ? theme.palette.action.selected
                          : "transparent",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.text.primary,
                        lineHeight: 1.4,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </SimpleItemChild>
                ))}
              </Box>
            </SimpleItemChildrenContainer>
          </Box>
        )}
      </SidebarHeader>

      <Divider sx={{ mx: 2, mb: 1 }} />

      {/* Sections */}
      <SidebarBody component="nav" {...SIDEBAR_NAV_TEST_PROPS}>
        {sections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {sectionIndex > 0 && <Divider sx={{ mx: 1, my: 1.5 }} />}
            <SidebarSectionWrapper>
              <SidebarSectionLabel {...getSectionTestProps(section.testKey)}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: theme.palette.text.tertiary,
                  }}
                >
                  {section.label}
                </Typography>
              </SidebarSectionLabel>

              {section.type === "cards" &&
                section.items.map((item, itemIndex) => (
                  <SidebarMenuCardCst key={itemIndex} {...item} />
                ))}

              {section.type === "simple" &&
                section.simpleItems?.map((item, itemIndex) => (
                  <SidebarSimpleItem key={itemIndex} {...item} />
                ))}
            </SidebarSectionWrapper>
          </React.Fragment>
        ))}
      </SidebarBody>
    </SidebarContainer>
  );
};

export default SidebarCst;
